import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
const promoteParkedImage = (image) => {
  if (!(image instanceof HTMLImageElement)) return;

  const { src, srcset } = image.dataset;

  if (srcset) {
    image.srcset = srcset;
    delete image.dataset.srcset;
  }

  if (src) {
    image.src = src;
    delete image.dataset.src;
  }
};

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#watchParkedImages();
  }

  #controller = new AbortController();
  #parkedImagesObserver = null;

  #parkedImages() {
    return [...this.querySelectorAll('img[data-src], img[data-srcset]')];
  }

  #watchParkedImages() {
    const parked = this.#parkedImages();

    if (!parked.length) return;

    const { signal } = this.#controller;
    const options = { signal, passive: true };

    this.#parkedImagesObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          this.#parkedImagesObserver?.unobserve(entry.target);
          promoteParkedImage(entry.target);
        }
      },
      { rootMargin: '200px' }
    );

    for (const image of parked) this.#parkedImagesObserver.observe(image);

    this.addEventListener('pointerenter', this.#promoteAllParkedImages, options);
    this.addEventListener('touchstart', this.#promoteAllParkedImages, options);
    this.addEventListener('focusin', this.#promoteAllParkedImages, options);
    this.addEventListener('slideshow:select', this.#onSlideshowSelect, options);
  }

  #onSlideshowSelect = (event) => {
    if (event.detail?.userInitiated) this.#promoteAllParkedImages();
  };

  #promoteAllParkedImages = () => {
    for (const image of this.#parkedImages()) promoteParkedImage(image);

    this.#parkedImagesObserver?.disconnect();
    this.removeEventListener('pointerenter', this.#promoteAllParkedImages);
    this.removeEventListener('touchstart', this.#promoteAllParkedImages);
    this.removeEventListener('focusin', this.#promoteAllParkedImages);
    this.removeEventListener('slideshow:select', this.#onSlideshowSelect);
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
